import React, { useEffect, useState, useRef } from 'react';
import HeroSection from '../components/HeroSection';
import TreatmentCaseSlider from '../components/TreatmentCaseSlider';
import { useLocation, useNavigate } from 'react-router-dom';

const NaturalTeethPage = () => {
  // URL 파라미터에서 초기 탭 설정 (깜빡임 방지)
  const location = useLocation();
  const navigate = useNavigate();
  
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    return tabParam || 'resin'; // 기본값: 'resin'
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderHeight, setSliderHeight] = useState('auto');
  const heroRef = useRef(null);
  const slideRefs = useRef([]);

  // URL 변경 시 탭 상태 업데이트
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    const newTab = tabParam || 'resin';
    setActiveTab(newTab);
  }, [location.search]);

  useEffect(() => {
    // fullPage.js 제거 (혹시 남아있을 경우)
    if (window.$ && window.$.fn && window.$.fn.fullpage && window.$.fn.fullpage.destroy) {
      window.$.fn.fullpage.destroy('all');
    }
    
    // body에서 fullpage-enabled 클래스 제거
    document.body.classList.remove('fullpage-enabled');
    
    // 일반 스크롤 복원을 위한 스타일 추가
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    
    // cleanup
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // activeTab이 변경될 때마다 페이지를 맨 위로 스크롤
  useEffect(() => {
    // DOM 업데이트 후 스크롤하도록 지연
    const timer = setTimeout(() => {
      if (heroRef.current) {
        heroRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'start'
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'auto'
        });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [activeTab]);

  // 탭이 변경될 때 슬라이드 초기화
  useEffect(() => {
    setCurrentSlide(0);
  }, [activeTab]);

  // 슬라이드 높이 조정
  useEffect(() => {
    const updateSliderHeight = () => {
      const currentSlideElement = slideRefs.current[currentSlide];
      if (currentSlideElement) {
        const slideHeight = currentSlideElement.offsetHeight;
        setSliderHeight(`${slideHeight}px`);
      }
    };

    // DOM 업데이트 후 높이 계산
    const timer = setTimeout(updateSliderHeight, 100);
    
    // 윈도우 리사이즈 시에도 높이 재계산
    window.addEventListener('resize', updateSliderHeight);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateSliderHeight);
    };
  }, [currentSlide, activeTab]);

  const tabs = [
    { id: 'resin', name: '레진치료', shortName: '레진' },
    { id: 'nerve', name: '신경치료', shortName: '신경' },
    { id: 're-nerve', name: '재신경치료', shortName: '재신경' },
    { id: 'prosthetic', name: '보철치료', shortName: '보철' },
    { id: 'periodontal', name: '치주치료', shortName: '치주' },
  ];

  // 탭 변경 핸들러 (URL도 함께 업데이트)
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/natural-teeth?tab=${tabId}`, { replace: true });
  };

  // 레진치료 사례 데이터
  const resinCases = [
    {
      id: '01',
      age: '10대',
      reason: '치아 사이가 썩었어요',
      treatment: '충치제거 후 레진 충전 치료',
      beforeImage: '/assets/자연치아 살리기/레진치료/레진치료_04_치료전.webp',
      afterImage: '/assets/자연치아 살리기/레진치료/레진치료_04_치료후1.webp'
    },
    {
      id: '02',
      age: '10대',
      reason: '치아에 구멍이 났어요',
      treatment: '충치제거 후 레진 충전치료 (보험레진 적용)',
      beforeImage: '/assets/자연치아 살리기/레진치료/레진치료_01_치료전.webp',
      afterImage: '/assets/자연치아 살리기/레진치료/레진치료_01_치료후.webp'
    },
    {
      id: '03',
      age: '30대',
      reason: '앞니 사이에 충치가 있어요',
      treatment: '치아 인접면 충치 제거 후 레진충전 치료',
      beforeImage: '/assets/자연치아 살리기/레진치료/레진치료_03_치료전1.webp',
      afterImage: '/assets/자연치아 살리기/레진치료/레진치료_03_치료후1.webp',
      afterImage2: '/assets/자연치아 살리기/레진치료/레진치료_03_치료후2.webp',
      followUp: '/assets/자연치아 살리기/레진치료/레진치료_03_치료후6년뒤1.webp'
    },
    {
      id: '04',
      age: '20대',
      reason: '앞니가 썩어 구멍이 났어요',
      treatment: '충치제거 후 레진충전치료',
      beforeImage: '/assets/자연치아 살리기/레진치료/레진치료_05_치료전.webp',
      afterImage: '/assets/자연치아 살리기/레진치료/레진치료_05_치료후.webp'
    },
    {
      id: '05',
      age: '30대',
      reason: '앞니 사이가 썩었어요',
      treatment: '충치제거 후 레진충전치료',
      beforeImage: '/assets/자연치아 살리기/레진치료/레진치료_06_치료전.webp',
      afterImage: '/assets/자연치아 살리기/레진치료/레진치료_06_치료후.webp'
    },
    {
      id: '06',
      age: '40대',
      reason: '치아 잇몸부위가 시리고 충치가 보여요',
      treatment: '충치제거 후 치경부 마모증 레진 충전 치료',
      beforeImage: '/assets/자연치아 살리기/레진치료/레진치료_07_치료전.webp',
      afterImage: '/assets/자연치아 살리기/레진치료/레진치료_07_치료후.webp'
    },
    {
      id: '07',
      age: '30대',
      reason: '치아 잇몸부위에 떼워놓은 보험재료가 떨어졌어요',
      treatment: '보험재료 제거 후 치경부 마모증 레진 충전 치료',
      beforeImage: '/assets/자연치아 살리기/레진치료/레진치료_08_치료전.webp',
      afterImage: '/assets/자연치아 살리기/레진치료/레진치료_08_치료후.webp'
    },
    {
      id: '08',
      age: '60대',
      reason: '앞니가 많이 닳았는데 떼우고 싶어요',
      treatment: '마모부위 레진 충전 치료',
      beforeImage: '/assets/자연치아 살리기/레진치료/레진치료_09_치료전.webp',
      afterImage: '/assets/자연치아 살리기/레진치료/레진치료_09_치료후.webp'
    }
  ];

  // 신경치료 사례 데이터
  const nerveCases = [
    {
      id: '01',
      age: '10대',
      reason: '타치과에서 충치치료한 치아에 통증이 있고, 잇몸에 고름주머니가 잡혔어요',
      treatment: '신경치료 + 크라운 보철치료',
      beforeImage: '/assets/자연치아 살리기/신경치료/신경치료_01_치료전.webp',
      afterImage: '/assets/자연치아 살리기/신경치료/신경치료_01_치료후.webp',
      beforeDescription: '치아 신경 괴사로 인해 뿌리 주변 잇몸뼈가 녹아 고름이 차있는 상태',
      afterDescription: '신경치료 후 염증이 깨끗이 나아 잇몸뼈가 회복됨'
    },
    {
      id: '02',
      age: '10대',
      reason: '갑자기 이가 너무 아파서 잠을 못잤어요',
      treatment: '치외치 신경치료 + 레진 코어',
      beforeImage: '/assets/자연치아 살리기/신경치료/신경치료_02_치료전.webp',
      afterImage: '/assets/자연치아 살리기/신경치료/신경치료_02_치료후.webp',
      beforeDescription: '치외치 감염으로 인해 뿌리 끝 염증이 잡힌 상황',
      afterDescription: '5년이 지나도 재감염없이 깨끗이 유지되고 있습니다'
    }
  ];

  // 재신경치료 사례 데이터
  const reNerveCases = [
    {
      id: '01',
      age: '30대',
      reason: '신경치료 했던 치아가 아픈데, 치료한 치과에서 발치하라고 했어요. 대학병원에서 재신경치료까지 받았는데 또 염증이 생겼어요',
      treatment: '재신경치료',
      beforeImage: '/assets/자연치아 살리기/재신경치료/재신경치료_04_치료전.webp',
      afterImage: '/assets/자연치아 살리기/재신경치료/재신경치료_04_치료후.webp',
      beforeDescription: '처음 신경치료를 했던 치과에서 발치 설명을 들은 후 대학병원에서 재신경치료를 받고 염증이 재발되어 한번 더 치아를 살려보고 싶다고 본원에 내원하심',
      afterDescription: '본원에서 재신경치료를 한번 더 시행한 후 뿌리 끝 염증이 모두 해소, 잇몸뼈까지 완벽하게 재생된 모습'
    },
    {
      id: '02',
      age: '30대',
      reason: '신경치료 한 치아 잇몸에서 고름이 나와요',
      treatment: '치외치 신경치료 + 크라운',
      beforeImage: '/assets/자연치아 살리기/재신경치료/재신경치료_03_치료전1.webp',
      beforeImage2: '/assets/자연치아 살리기/재신경치료/재신경치료_03_치료전2.webp',
      afterImage: '/assets/자연치아 살리기/재신경치료/재신경치료_03_치료후1.webp',
      afterImage2: '/assets/자연치아 살리기/재신경치료/재신경치료_03_치료후2.webp',
      beforeDescription: '신경치료한 치아 염증으로 인해 뿌리 끝 잇몸뼈가 녹고 고름이 차있는 상태',
      afterDescription: '재신경치료를 통해 뿌리 끝 염증 제거 후 잇몸뼈가 회복됨'
    },
    {
      id: '03',
      age: '30대',
      reason: '신경치료한 치아 잇몸에 고름주머니가 잡혔어요',
      treatment: '재신경치료 + 크라운 보철치료',
      beforeImage: '/assets/자연치아 살리기/재신경치료/재신경치료_01_치료전.webp',
      afterImage: '/assets/자연치아 살리기/재신경치료/재신경치료_01_치료후.webp'
    },
    {
      id: '04',
      age: '50대',
      reason: '신경치료한 치아가 아파요',
      treatment: '재신경치료 + 크라운 보철치료',
      beforeImage: '/assets/자연치아 살리기/재신경치료/재신경치료_02_치료전.webp',
      afterImage: '/assets/자연치아 살리기/재신경치료/재신경치료_02_치료후.webp'
    }
  ];

  // 보철치료 사례 데이터
  const prostheticCases = [
    {
      id: '01',
      age: '40대',
      reason: '음식을 잘못 씹어 앞니가 깨지면서 뿌리만 남았어요',
      treatment: '신경치료 후 포스트식립 및 크라운 치료',
      beforeImage: '/assets/자연치아 살리기/보철치료/보철치료_01_치료전.webp',
      duringImage: '/assets/자연치아 살리기/보철치료/보철치료_01_치료중.webp',
      afterImage: '/assets/자연치아 살리기/보철치료/보철치료_01_치료후.webp',
      overallDescription: '잇몸부위까지 부러진 치아에 기둥(post)를 넣고 레진을 쌓아올려 치아 머리를 회복, 크라운으로 치료 마무리 합니다.'
    },
    {
      id: '02',
      age: '40대',
      reason: '예전에 씌운 크라운이 부러졌어요',
      treatment: '포스트식립 및 크라운 치료',
      beforeImage: '/assets/자연치아 살리기/보철치료/보철치료_02_치료전.webp',
      duringImage: '/assets/자연치아 살리기/보철치료/보철치료_02_치료중.webp',
      afterImage: '/assets/자연치아 살리기/보철치료/보철치료_02_치료후.webp',
      overallDescription: '잇몸부위까지 부러진 치아에 기둥(post)를 넣고 레진을 쌓아올려 치아 머리를 회복, 크라운으로 치료 마무리 합니다.'
    }
  ];

  // 치주치료 사례 데이터
  const periodontalCases = [
    {
      id: '01',
      age: '40대',
      reason: '이가 너무 많이 상했어요',
      treatment: '전악 치석제거 및 잇몸치료 + 임플란트 치료',
      beforeImage: '/assets/자연치아 살리기/치주치료/치주치료_01_치료전.webp',
      afterImage: '/assets/자연치아 살리기/치주치료/치주치료_01_치료후.webp'
    },
    {
      id: '02',
      age: '40대',
      reason: '잇몸이 너무 붓고 피가 많아 나요',
      treatment: '전악 치석제거 및 잇몸치료 + 충치치료 및 부분교정',
      beforeImage: '/assets/자연치아 살리기/치주치료/치주치료_02_치료전.webp',
      afterImage: '/assets/자연치아 살리기/치주치료/치주치료_02_치료후.webp'
    },
    {
      id: '03',
      age: '60대',
      reason: '잇몸이 너무 붓고 피가 많아 나요',
      treatment: '전악 치석제거 및 잇몸치료 + 임플란트 치료',
      beforeImage: '/assets/자연치아 살리기/치주치료/치주치료_03_치료전.webp',
      afterImage: '/assets/자연치아 살리기/치주치료/치주치료_03_치료후.webp'
    }
  ];

  const renderResinContent = () => (
    <div className="space-y-16">
      {/* 레진치료 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">레진치료</h2>
        <div className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
          <p className="mb-4">단순히 떼우는 치료라구요?</p>
          <p className="mb-4"><strong className="text-primary">레진치료에도 차이는 발생합니다.</strong></p>
          <p className="mb-4">
            깨끗한 충치 제거, 방습, 그리고 꼼꼼한 레진 충전과 치아 외형과 교합을 회복하는 연마가 완벽하게 이루어질 때 <strong className="text-primary">레진치료는 지속성을 갖습니다.</strong>
          </p>
          <p className="text-lg">
            <strong className="text-secondary">정확한 눈썰미와 섬세한 손기술</strong>로<br />
            자연치아와 구분되지 않는 완벽한 레진치료를 제공합니다.
          </p>
        </div>

        {/* 레진치료의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">당일 치료</h4>
            <p className="text-gray-600 text-sm">한 번의 내원으로 치료 완료 가능</p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">자연스러운 색상</h4>
            <p className="text-gray-600 text-sm">치아색과 동일한 심미적 복원</p>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">보험 적용</h4>
            <p className="text-gray-600 text-sm">12세 미만 영구치만 건강보험 적용</p>
          </div>
        </div>
      </div>

      {/* 치료 사례들 */}
      <TreatmentCaseSlider
        cases={resinCases}
        currentSlide={currentSlide}
        setCurrentSlide={setCurrentSlide}
        sliderHeight={sliderHeight}
        slideRefs={slideRefs}
        gradientColors="from-blue-50 to-purple-50"
        title="치료 사례"
      />

      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">레진치료 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          충치가 있거나 치아가 깨졌다면 빠른 치료가 중요합니다. 
          자연치아를 살리는 레진치료로 건강한 치아를 되찾으세요.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderNerveContent = () => (
    <div className="space-y-16">
      {/* 신경치료 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">신경치료</h2>
        <div className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed mb-8">
          <p className="mb-4"><strong className="text-gray-800">내 치아를 살리기 위한 마지막 치료?</strong></p>
          <p className="mb-4">
            충치가 이미 많이 진행된 경우, 그로 인해 통증이 있거나 염증이 생기고 신경이 괴사된 경우,<br />
            치아가 파절된 경우, 치아에 금이가서 씹을때마다 찌릿한 통증이 있는 경우<br />
            <strong className="text-primary">신경치료를 하게 되는 이유는 다양합니다.</strong>
          </p>
          <p className="mb-4">
            다만, 여러 가지 원인에 의해 치아가 많이 손상된 경우 치아를 발치하지 않고 살려쓰기 위해<br />
            <strong className="text-secondary">'어쩔 수 없이' 해야만 하는 것이 신경치료입니다.</strong>
          </p>
          <p className="mb-4">
            건강하지 않은 치아의 신경을 제거하고 깨끗하고 소독하여 저작 기능을 회복시키는 것이 신경치료의 목적이고,<br />
            <strong className="text-primary">꼼꼼하게 잘 치료해야만 신경치료의 성공률이 올라갑니다.</strong>
          </p>
        </div>

        {/* 신경치료의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
          <div className="bg-red-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">발치 예방</h4>
            <p className="text-gray-600 text-sm">치아를 뽑지 않고 살리는 마지막 방법</p>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">정석 치료</h4>
            <p className="text-gray-600 text-sm">디펄핀 없는 대학병원 수준의 치료</p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">기능 회복</h4>
            <p className="text-gray-600 text-sm">씹는 기능을 다시 되찾을 수 있습니다</p>
          </div>
        </div>

        {/* 한앤손 부부치과 부부치과만의 신경치료 */}
        <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-primary mb-4">한앤손 부부치과만의 신경치료</h3>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <p className="text-lg text-gray-700">
              <strong className="text-secondary">저희 치과는 디펄핀(신경괴사약제)를 절대 사용하지 않고,<br />
              대학병원 수준의 정석 치료를 시행합니다.</strong>
            </p>
          </div>
        </div>
      </div>

      {/* 치료 사례들 */}
      <TreatmentCaseSlider
        cases={nerveCases}
        currentSlide={currentSlide}
        setCurrentSlide={setCurrentSlide}
        sliderHeight={sliderHeight}
        slideRefs={slideRefs}
        gradientColors="from-red-50 to-orange-50"
        title="치료 사례"
        showImageDescriptions={true}
      />

      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">신경치료 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          치아 통증이나 염증으로 고생하고 계신가요? 
          치아를 뽑지 않고 살릴 수 있는 신경치료로 소중한 치아를 지켜보세요.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderReNerveContent = () => (
    <div className="space-y-16">
      {/* 재신경치료 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">재신경치료</h2>
        <div className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed mb-8">
          <p className="mb-4">
            <strong className="text-gray-800">이미 신경치료를 한 치아를 살리고 싶으면 대학병원에 가서 재신경치료를 받아보라고 했어요.</strong>
          </p>
          <p className="mb-4">
            <strong className="text-secondary">재신경치료는 미세현미경이 있는 대학병원에서만 가능하다?</strong>
          </p>
          <p className="mb-4">
            <strong className="text-primary">한앤손 부부치과에서는 이미 많은 분들이 재신경치료를 통해 자연치아를 살려쓰고 계십니다.</strong>
          </p>
          <p className="mb-4">
            신경치료가 잘 되었어도, 우리 치아는 다시 저작기능을 하며 여러 가지 원인에 의해 염증이 생기는 경우가 있습니다.
          </p>
          <p className="mb-4">
            <strong className="text-primary">3차원 CT 촬영</strong>을 통해 치아의 어느 뿌리에 염증이 생겼는지, 염증의 크기가 어느 정도인지, 
            혹은 뿌리에 금이 갔는지 진단 후 가능한 경우 <strong className="text-secondary">재신경치료를 통해 치아의 수명을 늘릴 수 있습니다.</strong>
          </p>
        </div>

        {/* 재신경치료의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
          <div className="bg-purple-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">3차원 CT 진단</h4>
            <p className="text-gray-600 text-sm">정확한 진단을 위한 3D 영상 분석</p>
          </div>
          
          <div className="bg-indigo-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">대학병원급 치료</h4>
            <p className="text-gray-600 text-sm">미세현미경 없이도 가능한 전문 치료</p>
          </div>
          
          <div className="bg-teal-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">치아 수명 연장</h4>
            <p className="text-gray-600 text-sm">이미 치료한 치아도 다시 살릴 수 있습니다</p>
          </div>
        </div>

        {/* 한앤손 부부치과의 재신경치료 */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-primary mb-4">한앤손 부부치과의 재신경치료</h3>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <p className="text-lg text-gray-700 mb-3">
              <strong className="text-secondary">대학병원에만 가능하다고 생각하셨나요?</strong>
            </p>
            <p className="text-lg text-gray-700">
              저희는 <strong className="text-primary">3차원 CT와 풍부한 임상경험</strong>을 바탕으로<br />
              많은 환자분들의 치아를 성공적으로 재치료하고 있습니다.
            </p>
          </div>
        </div>
      </div>

      {/* 치료 사례들 */}
      <div>
        <h3 className="text-3xl font-bold text-center text-primary mb-2">치료 사례</h3>
        <p className="text-center text-lg text-secondary mb-8 font-medium">3차원 CT로 염증 상태를 확인하고 치료합니다</p>
      <TreatmentCaseSlider
        cases={reNerveCases}
        currentSlide={currentSlide}
        setCurrentSlide={setCurrentSlide}
        sliderHeight={sliderHeight}
        slideRefs={slideRefs}
        gradientColors="from-purple-50 to-indigo-50"
          title=""
          showImageDescriptions={true}
      />
      </div>

      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">재신경치료 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          이미 신경치료를 받은 치아에 다시 문제가 생겼나요? 
          발치하기 전에 재신경치료로 한 번 더 치아를 살려보세요.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderProstheticContent = () => (
    <div className="space-y-16">
      {/* 보철치료 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">보철치료</h2>
        <div className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
          <p className="mb-4">손상된 치아, 발치만이 해답일까요?</p>
          <p className="mb-4"><strong className="text-primary">정밀한 보철치료로 자연치아를 살릴 수 있습니다.</strong></p>
          <p className="text-lg">
            크라운, 인레이, 온레이 등 다양한 보철치료를 통해 손상된 치아의 형태와 기능을 회복하고<br />
            <strong className="text-primary">자연치아를 최대한 오래 보존할 수 있습니다.</strong>
          </p>
        </div>

        {/* 보철치료의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">정밀한 제작</h4>
            <p className="text-gray-600 text-sm">개인 맞춤형 정밀 보철물 제작</p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">자연치아 보존</h4>
            <p className="text-gray-600 text-sm">발치하지 않고 치아 살리기</p>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">기능 회복</h4>
            <p className="text-gray-600 text-sm">씹는 기능과 심미성 동시 개선</p>
          </div>
        </div>
      </div>

      {/* 보철치료 종류 */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">보철치료의 종류</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">크라운</h4>
            <p className="text-gray-600 text-sm mb-3">
              치아 전체를 덮는 보철물로 심하게 손상된 치아의 형태와 기능을 완전히 회복합니다.
            </p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>• 신경치료 후 치아 보호</li>
              <li>• 큰 충치로 인한 치아 손상</li>
              <li>• 치아 파절 복원</li>
            </ul>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">인레이/온레이</h4>
            <p className="text-gray-600 text-sm mb-3">
              치아의 일부분만 덮는 보철물로 건강한 치아 조직을 최대한 보존합니다.
            </p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>• 중간 크기의 충치 치료</li>
              <li>• 기존 아말감 교체</li>
              <li>• 치아 균열 복원</li>
            </ul>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">브리지</h4>
            <p className="text-gray-600 text-sm mb-3">
              인접한 건강한 치아를 이용하여 상실된 치아를 대체하는 보철물입니다.
            </p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>• 1~2개 치아 상실</li>
              <li>• 임플란트 대안</li>
              <li>• 연결된 치아 복원</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 치료 사례들 */}
      <TreatmentCaseSlider
        cases={prostheticCases}
        currentSlide={currentSlide}
        setCurrentSlide={setCurrentSlide}
        sliderHeight={sliderHeight}
        slideRefs={slideRefs}
        gradientColors="from-blue-50 to-purple-50"
        title="치료 사례"
        showDuringImage={true}
        showOverallDescription={true}
      />
      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">보철치료 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          손상된 치아 때문에 고민이신가요? 
          발치하기 전에 정밀한 보철치료로 소중한 자연치아를 지켜보세요.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderPeriodontalContent = () => (
    <div className="space-y-16">
      {/* 치주치료 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">치주치료</h2>
        <div className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
          <p className="mb-4">아무리 건강한 치아라 할지라도 치아 뿌리를 받쳐주는 치조골과 잇몸이 무너지면 저작기능을 잃게 됩니다.</p>
          <p className="mb-4"><strong className="text-primary">스케일링과 잇몸치료로 잇몸 건강을 되찾으세요.</strong></p>
          <p className="text-lg">
            치은연상의 치석을 제거하는 <strong>스케일링</strong>,<br />
            그리고 치아뿌리에 침착되어 치주염을 유발하는 치은연하치석을 제거하는 <strong>잇몸치료</strong>를 통해<br />
            건강한 잇몸과 치조골을 유지할 수 있습니다.
          </p>
        </div>
        {/* 치주치료의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">숙련된 위생사</h4>
            <p className="text-gray-600 text-sm">경험 많은 치과위생사가 직접 시술</p>
          </div>
          <div className="bg-green-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">마취 가글액 사용</h4>
            <p className="text-gray-600 text-sm">잇몸 표면 마취로 통증 최소화</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">치주질환 예방</h4>
            <p className="text-gray-600 text-sm">정기적 관리로 치아수명 연장</p>
          </div>
        </div>
      </div>
      {/* 치료 사례들 */}
      <TreatmentCaseSlider
        cases={periodontalCases}
        currentSlide={currentSlide}
        setCurrentSlide={setCurrentSlide}
        sliderHeight={sliderHeight}
        slideRefs={slideRefs}
        gradientColors="from-blue-50 to-purple-50"
        title="치료 사례"
      />
      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">치주치료 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          한앤손 부부치과는 치석제거시 불편감을 덜어드리기 위해 마취 가글액을 사용하여 잇몸 표면 마취 후 숙련된 치과위생사 선생님께서 스케일링을 도와드립니다.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );



  const renderTabContent = () => {
    switch (activeTab) {
      case 'resin':
        return renderResinContent();
      case 'nerve':
        return renderNerveContent();
      case 're-nerve':
        return renderReNerveContent();
      case 'prosthetic':
        return renderProstheticContent();
      case 'periodontal':
        return renderPeriodontalContent();
      default:
        return renderResinContent();
    }
  };

  return (
    <div className="natural-teeth-page-container">
      {/* 히어로 섹션 */}
      <HeroSection 
        title="자연치아 살리기"
        subtitle="소중한 내 치아를 끝까지 보존합니다"
        backgroundImage="/assets/자연치아배경.png"
        brandText="한앤손 부부치과"
        ref={heroRef}
      >
        {/* 탭 네비게이션 */}
        <div className="flex justify-center">
          <div className="bg-white/20 backdrop-blur-md rounded-2xl p-1 inline-flex max-w-full border border-white/30">
            <div className="flex space-x-1 overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-2.5 md:px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap min-w-0 flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-white text-primary shadow-md'
                      : 'text-white/90 hover:text-white hover:bg-white/20'
                  }`}
                >
                  <span className="block md:hidden">{tab.shortName}</span>
                  <span className="hidden md:block">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </HeroSection>

      {/* 콘텐츠 영역 */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="transition-all duration-300">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default NaturalTeethPage; 